/**
 * Factory Selector Component
 *
 * Always visible in the navbar. Shows the active factory name.
 * - Single factory: non-interactive badge with a tooltip (clear context, no false affordance)
 * - Multiple factories: full dropdown to switch between them
 */

import { useEffect, JSX } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Factory as FactoryIcon, Check, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthProvider';
import { currentFactoryIdAtom, isMultiTenantAtom } from '@/stores/atoms';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import { notify } from '@/lib/notify';

export function FactorySelector(): JSX.Element {
  const { t } = useTranslation();
  const { availableFactories, selectFactory } = useAuth();
  const currentFactoryId = useAtomValue(currentFactoryIdAtom);
  const setCurrentFactoryId = useSetAtom(currentFactoryIdAtom);
  const isMultiFactory = useAtomValue(isMultiTenantAtom);

  // Fallback: if AuthProvider's auto-select didn't fire (e.g. stale closure race),
  // directly set the atom when exactly one factory is available.
  useEffect(() => {
    if (availableFactories.length === 1 && !currentFactoryId) {
      setCurrentFactoryId(availableFactories[0].id);
    }
  }, [availableFactories, currentFactoryId, setCurrentFactoryId]);

  const currentFactory = availableFactories.find(f => f.id === currentFactoryId);
  const displayName = currentFactory?.name ?? t('factory.selector.selectFactory');

  async function handleSelectFactory(factoryId: string): Promise<void> {
    if (factoryId === currentFactoryId) {
      return;
    }

    try {
      await selectFactory({ factoryId });
      notify.success(t('factory.selector.switched'));
    } catch (_error) {
      notify.error(t('factory.selector.switchError'));
    }
  }

  // Single factory: static badge — provides context without implying interactivity
  if (!isMultiFactory) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-3 py-1.5 text-sm font-medium text-muted-foreground select-none cursor-default"
              aria-label={t('factory.selector.currentFactory', { name: displayName })}
            >
              <FactoryIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden md:inline max-w-[160px] truncate">{displayName}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{t('factory.selector.singleFactory')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Multiple factories: full interactive dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <FactoryIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden md:inline max-w-[160px] truncate">{displayName}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('factory.selector.title')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableFactories.map(factory => (
          <DropdownMenuItem
            key={factory.id}
            onClick={() => handleSelectFactory(factory.id)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <span>{factory.name}</span>
              {factory.id === currentFactoryId && <Check className="h-4 w-4" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
